/**
 * What a message is about, at a glance — the bucket the person controls in
 * Settings (heroes, system, social, alerts), drawn as business-web's
 * Notifications draws a category: an icon in its own tone. The badge on a
 * row's avatar, the chip in the reader. The words are the Settings ones, so a
 * message's kind reads the same where it can be switched off.
 */
import { AlertTriangle, Info, Sparkles, Users } from '@tonnie/assets';
import { useTranslation } from '@tonnie/locale';
import type { NotificationBucket } from '@tonnie/types';
import { cn } from '@tonnie/ui';
import type { TFunction } from 'i18next';

const VISUAL: Record<
  NotificationBucket,
  { icon: React.ComponentType<{ className?: string }>; tone: string }
> = {
  hero: { icon: Sparkles, tone: 'bg-tone-violet-subtle text-tone-violet' },
  system: { icon: Info, tone: 'bg-tone-blue-subtle text-tone-blue' },
  social: { icon: Users, tone: 'bg-tone-green-subtle text-tone-green' },
  alert: { icon: AlertTriangle, tone: 'bg-tone-amber-subtle text-tone-amber' },
};

/** Literal keys, so the key-usage gate checks every one against en.json. */
export function inboxCategoryLabel(t: TFunction, category: NotificationBucket): string {
  switch (category) {
    case 'hero':
      return t('profile.notifCatHero');
    case 'system':
      return t('profile.notifCatSystem');
    case 'social':
      return t('profile.notifCatSocial');
    case 'alert':
      return t('profile.notifCatAlert');
    default: {
      const unreachable: never = category;
      return unreachable;
    }
  }
}

/** The category's mark on a row's avatar — decorative; the row says the words. */
export function InboxCategoryBadge({ category }: { category: NotificationBucket }) {
  const { icon: Icon, tone } = VISUAL[category];
  return (
    <span
      aria-hidden
      data-inbox-category={category}
      className={cn(
        'absolute -bottom-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full ring-2 ring-card',
        tone
      )}
    >
      <Icon className="h-2.5 w-2.5" />
    </span>
  );
}

/** The category, said — a chip with its icon in its tone. */
export function InboxCategoryChip({ category }: { category: NotificationBucket }) {
  const { t } = useTranslation();
  const { icon: Icon, tone } = VISUAL[category];
  return (
    <span
      data-testid="inbox-message-category"
      className={cn(
        'inline-flex items-center gap-1 rounded-pill px-2.5 py-0.5 text-xs font-medium',
        tone
      )}
    >
      <Icon className="h-3.5 w-3.5" aria-hidden />
      {inboxCategoryLabel(t, category)}
    </span>
  );
}
